import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { mapToEntityWithKey } from '../../utils/changes-to-keyed';
import { hasElements } from '../../utils/has-elements';
import { filterForNotIsNil, isNotNil } from '../../utils/is-not-nil';
import { Destroyable, MixinRoot } from '../../utils/mixins';
import { Perisitor } from '../../utils/persistor';
import { Dict } from '../models/dict';
import { Keyed } from '../models/keyed';
import { Organization } from '../models/organization';
import { DatabaseService } from './database.service';
import {AppConfigService} from "src/app/app.config.service";

@Injectable({
  providedIn: 'root',
})
export class OrganizationService extends Destroyable(MixinRoot) {
  constructor(private db: DatabaseService, private auth: AngularFireAuth, private appConfigService: AppConfigService) {
    super();

    this.auth.user
      .pipe(
        filterForNotIsNil(),
        map((u) => u.uid),
        distinctUntilChanged(),
        switchMap(() => this.getUserOrganizations().pipe(take(1)).toPromise()),
        takeUntil(this.destroyed$)
      )
      .subscribe((userOrgs) => {
        const currentOrgId = this.currOrgIdPersistor.get();
        let currentOrg;
        if (currentOrgId) {
          currentOrg = userOrgs.find((o) => o.key === currentOrgId);
        } else if (hasElements(userOrgs)) {
          currentOrg = userOrgs[0];
        }

        if (currentOrg) {
          appConfigService.changeOrgBaseMuxerServiceURL(currentOrg.baseMuxerServiceURL);
          this._currentOrgId.next(currentOrg.key);
        }
      });
  }

  // CRUD methods
  // -----------------------------------------------------------------------------------------------------------------------------

  create(organization: Organization) {
    return this.db.organizations.add({
      title: organization.title,
      baseMuxerServiceURL: organization.baseMuxerServiceURL,
      description: organization.description,
      archivesEnabled: organization.archivesEnabled,
    });
  }

  update(organization: Keyed<Partial<Organization>>): Promise<void> {
    const { key, ...rest } = organization;
    return this.db.organizations.update(key, rest);
  }

  async remove(organizationId: string) {
    const users = await this.db.users.getList().pipe(take(1)).toPromise();
    const organizationUserIds = (users || [])
      .filter((user) => user.organizations?.[organizationId])
      .map((user) => user.uid);

    const removeUsersOrganizations = organizationUserIds.map((uid) =>
      this.db.user(uid).organization(organizationId).remove()
    );

    return Promise.all([
      ...removeUsersOrganizations,
      this.db.organizations.remove(organizationId),
      this.db.organization(organizationId).users.remove(),
    ]);
  }

  private getAllOrganizations(): Observable<Dict<Organization>> {
    return this.db.organizations
      .getDict()
      .valueChanges()
      .pipe(map((orgs) => orgs ?? {}));
  }

  getOrganizationsList(): Observable<Keyed<Organization>[]> {
    return this.getAllOrganizations().pipe(
      map((orgById) =>
        Object.entries(orgById)
          .map(([key, org]) => ({ ...org, key }))
          .sort((a, b) => a.title.localeCompare(b.title))
      )
    );
  }

  getUserOrganizations(): Observable<Keyed<Organization>[]> {
    const userOrganizationIds = this.auth.user.pipe(
      filter(isNotNil),
      switchMap((user) => this.db.user(user.uid).organizations()),
      map((userOrgs) => (userOrgs ? Object.keys(userOrgs) : []))
    );

    return combineLatest([this.getAllOrganizations(), userOrganizationIds]).pipe(
      map(([allOrgs, userOrgIds]) =>
        userOrgIds
          .map((id) => ({ key: id, ...allOrgs[id] }))
          .sort((o1, o2) => o1.title.localeCompare(o2.title))
      )
    );
  }

  // Current Organization
  // -----------------------------------------------------------------------------------------------------------------------------

  private currOrgIdPersistor = new Perisitor<string>('mk-current-org');
  private _currentOrgId = new ReplaySubject<string>(1);

  setCurrentOrganization(organization: Keyed<Organization>): void {
    this._currentOrgId.next(organization.key);
    this.currOrgIdPersistor.set(organization.key);
    this.appConfigService.changeOrgBaseMuxerServiceURL(organization.baseMuxerServiceURL);
  }

  get currentOrganizationId() {
    return this._currentOrgId.asObservable();
  }

  get currentOrganization(): Observable<Keyed<Organization>> {
    return this._currentOrgId.pipe(switchMap((id) => this.getOrganization(id)));
  }

  // Private
  // -----------------------------------------------------------------------------------------------------------------------------
  private getOrganization(id: string): Observable<Keyed<Organization>> {
    return this.db.organizations.get(id).snapshotChanges().pipe(mapToEntityWithKey());
  }
}
